import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { axiosInstance as axios } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Plus, UserPlus, Heart, TreePine } from 'lucide-react';
import { buildGenerations, GEN_LABELS } from './tree/familyTreeHelpers';
import { PersonCard } from './tree/PersonCard';
import { TreeConnectors } from './tree/TreeConnectors';
import { NodeFormModal } from './tree/NodeFormModal';

const API = '/api';

const TREE_BANNER = 'https://static.prod-images.emergentagent.com/jobs/6e8c71c9-5deb-44de-959d-34c14877fd56/images/f6c57952bc950a5628b16f729102541a5e23578d044daa97a21963bc23747c8f.jpeg';

export function FamilyTreeTab({ familyId, family, currentUser, isOwner }) {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add-root');
  const [editingNode, setEditingNode] = useState(null);
  const [contextNode, setContextNode] = useState(null); // the node we're adding relative to
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const containerRef = useRef(null);
  const [makingRoot, setMakingRoot] = useState(false);
  const rootNode = nodes.find(n => n.is_root);
  const rootIsMe = !rootNode || rootNode.user_id === currentUser?.user_id;

  const fetchTree = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/families/${familyId}/family-tree`, { withCredentials: true });
      setNodes(res.data.nodes || []);
    } catch (err) {
      console.error('Error fetching tree:', err);
    } finally {
      setLoading(false);
    }
  }, [familyId]);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  const { generations, nodeMap, genMap } = useMemo(() => buildGenerations(nodes), [nodes]);

  // Track which family members are already in the tree
  const existingNodeUserIds = useMemo(() => {
    const ids = new Set();
    nodes.forEach(n => { if (n.user_id) ids.add(n.user_id); });
    return ids;
  }, [nodes]);

  const sortedGens = useMemo(() =>
    Object.keys(generations).map(Number).sort((a, b) => a - b),
    [generations]
  );

  /* ---- CRUD ---- */

  const handleSave = async (formData) => {
    try {
      if (modalMode === 'edit' && editingNode) {
        await axios.put(
          `${API}/families/${familyId}/family-tree/nodes/${editingNode.node_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Person updated');
      } else {
        const payload = { ...formData };
        if (modalMode === 'add-root') {
          payload.is_root = true;
        }
        if (modalMode === 'add-child' && contextNode) {
          // Child's parents = contextNode + contextNode's partner
          const parentIds = [contextNode.node_id];
          if (contextNode.partner_id) parentIds.push(contextNode.partner_id);
          payload.parent_ids = parentIds;
        }
        if (modalMode === 'add-parent' && contextNode) {
          // After creating the parent, we'll link the child
          const res = await axios.post(
            `${API}/families/${familyId}/family-tree/nodes`,
            payload,
            { withCredentials: true }
          );
          // Update the context node to have this new parent
          const newParentId = res.data.node_id;
          const currentParentIds = contextNode.parent_ids || [];
          if (currentParentIds.length < 2) {
            await axios.put(
              `${API}/families/${familyId}/family-tree/nodes/${contextNode.node_id}`,
              { parent_ids: [...currentParentIds, newParentId] },
              { withCredentials: true }
            );
          }
          toast.success(`${formData.name} added as parent`);
          setModalOpen(false);
          setEditingNode(null);
          setContextNode(null);
          fetchTree();
          return;
        }
        if (modalMode === 'add-partner' && contextNode) {
          payload.partner_id = contextNode.node_id;
        }
        await axios.post(
          `${API}/families/${familyId}/family-tree/nodes`,
          payload,
          { withCredentials: true }
        );
        toast.success(`${formData.name} added to tree`);
      }
      setModalOpen(false);
      setEditingNode(null);
      setContextNode(null);
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (node) => {
    try {
      await axios.delete(
        `${API}/families/${familyId}/family-tree/nodes/${node.node_id}`,
        { withCredentials: true }
      );
      toast.success(`${node.name} removed from tree`);
      setDeleteConfirm(null);
      fetchTree();
    } catch (err) {
      toast.error('Failed to remove person');
    }
  };

  const handleMakeRoot = async (node) => {
    if (makingRoot) return;
    setMakingRoot(true);
    try {
      await axios.put(
        `${API}/families/${familyId}/family-tree/nodes/${node.node_id}`,
        { is_root: true },
        { withCredentials: true }
      );
      toast.success(`The tree is now drawn from ${node.name}`);
      fetchTree();
    } catch (err) {
      toast.error('Could not change the Root');
    } finally {
      setMakingRoot(false);
    }
  };

  const openAdd = (mode, relativeNode = null) => {
    setModalMode(mode);
    setContextNode(relativeNode);
    setEditingNode(null);
    setModalOpen(true);
  };

  const openEdit = (node) => {
    setModalMode('edit');
    setEditingNode(node);
    setContextNode(null);
    setModalOpen(true);
  };

  // Auto-add the current user as root (no modal needed)
  const handleAddSelf = async () => {
    try {
      await axios.post(
        `${API}/families/${familyId}/family-tree/nodes`,
        {
          name: currentUser?.name || 'Me',
          gender: 'other',
          user_id: currentUser?.user_id,
          is_root: true,
        },
        { withCredentials: true }
      );
      toast.success('You\'ve been added as the root of the tree!');
      fetchTree();
    } catch (err) {
      toast.error('Failed to add yourself');
    }
  };

  /* ---- Render ---- */

  const banner = (
    <div className="mb-6 rounded-2xl overflow-hidden shadow-sm ring-1 ring-black/5" data-testid="family-tree-banner">
      <img
        src={TREE_BANNER}
        alt="A multi-generational family gathered beneath a majestic oak tree, symbolizing roots and branches"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="family-tree-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
      </div>
    );
  }

  // Empty state
  if (nodes.length === 0) {
    return (
      <div data-testid="family-tree-empty-wrap">
      {banner}
      <div className="text-center py-16 px-4" data-testid="family-tree-empty">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
          <TreePine className="w-10 h-10 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Start Your Family Tree</h3>
        <p className="text-gray-500 mb-6 max-w-md mx-auto">
          Tap the button to add yourself as the starting point, then connect your spouse, children, and parents.
        </p>
        {isOwner && (
          <Button
            onClick={handleAddSelf}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="start-tree-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Myself to Start
          </Button>
        )}
      </div>
      </div>
    );
  }

  return (
    <div data-testid="family-tree-container">
      {banner}
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <TreePine className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Family Tree</h2>
            <p className="text-sm text-gray-500">{nodes.length} {nodes.length === 1 ? 'person' : 'people'}</p>
          </div>
        </div>
        {isOwner && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => openAdd(nodes.length ? 'add-person' : 'add-root')}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="add-person-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Person
          </Button>
        )}
      </div>

      {/* Tree visualization */}
      <div
        ref={containerRef}
        className="relative overflow-x-auto pb-8"
        style={{ minHeight: `${sortedGens.length * 180 + 60}px` }}
      >
        <TreeConnectors
          generations={generations}
          nodeMap={nodeMap}
          genMap={genMap}
          containerRef={containerRef}
        />

        <div className="relative z-10 space-y-12">
          {sortedGens.map(gen => (
            <div key={gen} className="relative">
              {/* Generation label */}
              <div className="flex items-center justify-center mb-4">
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 uppercase tracking-wider">
                  {gen === 0 && !rootIsMe ? 'Root & Partner' : (GEN_LABELS[String(gen)] || `Generation ${gen > 0 ? '+' : ''}${gen}`)}
                </span>
              </div>

              {/* Nodes in this generation */}
              <div className="flex flex-wrap justify-center items-start gap-4 sm:gap-6">
                {generations[gen]?.map((node, idx) => {
                  const isPartnerOfPrev = idx > 0 && generations[gen][idx - 1]?.partner_id === node.node_id;
                  return (
                    <React.Fragment key={node.node_id}>
                      {isPartnerOfPrev && (
                        <div className="flex items-center -mx-2">
                          <Heart className="w-4 h-4 text-pink-400" />
                        </div>
                      )}
                      <PersonCard
                        node={node}
                        nodeMap={nodeMap}
                        isOwner={isOwner}
                        onEdit={openEdit}
                        onDelete={(n) => setDeleteConfirm(n)}
                        onAddChild={(n) => openAdd('add-child', n)}
                        onAddParent={(n) => openAdd('add-parent', n)}
                        onAddPartner={(n) => openAdd('add-partner', n)}
                        onMakeRoot={handleMakeRoot}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap items-center gap-4 justify-center text-xs text-gray-500">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-0.5 bg-pink-300" style={{ borderTop: '2px dashed #f9a8d4' }} />
          <span>Partner</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-0.5 bg-slate-400" />
          <span>Parent-Child</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-sky-500" />
          <span>Male</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-rose-400" />
          <span>Female</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span>Other</span>
        </div>
      </div>

      {/* Add / Edit modal */}
      <NodeFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        initialData={modalMode === 'edit' ? editingNode : null}
        mode={modalMode}
        familyMembers={family?.members}
        existingNodeUserIds={existingNodeUserIds}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-node-confirm">
          <DialogHeader>
            <DialogTitle>Remove {deleteConfirm?.name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            This will remove {deleteConfirm?.name} from the family tree. Their connections to other family members will be unlinked.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm)}
              data-testid="confirm-delete-node"
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FamilyTreeTab;
